/**
 * The pick-one lists' two rules: which choices a typed text keeps, and where a typed letter
 * jumps to in a list without a search box.
 *
 * Both fold case and accents, so a make written with a caron is found without one, and
 * "golf 7" finds "Golf 7 (2012-2020)". Every typed word has to start a word of the label,
 * so "o" in a make list keeps Opel, not every make with an "o" in it.
 *
 * PURE.
 */

export interface PickChoice {
  value: string;
  label: string;
  /** Extra `data-*` attributes on the choice's row, such as the price a form's draft reads. */
  attrs?: Readonly<Record<string, string>>;
}

/** The lists that get a search box: a short one is read faster than it is typed into. */
export const PICK_SEARCH_FROM = 9;

/** Letters NFKD does not take apart, written the way a keyboard without them would. */
const FOLDED: Readonly<Record<string, string>> = { ø: "o", æ: "ae", œ: "oe", ß: "ss", ł: "l", đ: "d", þ: "th", ı: "i" };
const FOLDED_RE = new RegExp(`[${Object.keys(FOLDED).join("")}]`, "g");

/** Lower case, accents off, every run of punctuation one space. */
export function foldLabel(text: string): string {
  return text
    .toLowerCase()
    .replace(FOLDED_RE, (c) => FOLDED[c] ?? c)
    .normalize("NFKD")
    .replace(/\p{M}+/gu, "")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();
}

/** The choices whose label has a word starting with each typed word, in their own order. */
export function matchChoices<C extends PickChoice>(choices: readonly C[], query: string): C[] {
  const folded = foldLabel(query);
  if (!folded) return [...choices];
  const words = folded.split(" ");
  return choices.filter((c) => {
    const hay = ` ${foldLabel(c.label)}`;
    return words.every((w) => hay.includes(` ${w}`));
  });
}

/**
 * Where type-ahead lands: the first label after `from` that starts with `typed`, wrapping
 * round to the top; -1 when none does. Typing the same letter again walks through every
 * label that starts with it, the way a native select does.
 */
export function typeAheadIndex(choices: readonly PickChoice[], from: number, typed: string): number {
  const want = foldLabel(typed);
  if (!want || !choices.length) return -1;
  const repeat = want.length > 1 && [...want].every((c) => c === want[0]);
  const prefix = repeat ? want[0]! : want;
  // A longer prefix keeps the current choice when it still fits; one letter moves on.
  const start = prefix.length > 1 ? from : from + 1;
  for (let step = 0; step < choices.length; step++) {
    const i = (((start + step) % choices.length) + choices.length) % choices.length;
    if (foldLabel(choices[i]!.label).startsWith(prefix)) return i;
  }
  return -1;
}
